import { useTranslation } from 'react-i18next';
import { useModalStore, useToast } from '@/shared/lib';
import { useBanksDictionary } from '@/shared/api';
import {
  type InvoiceDetailsProps,
  useDeleteInvoice,
  useInvoice,
  useInvoiceDownload,
} from '../api';
import { useInvoiceHistoryDialog } from '../lib';
import { useConfirmationModal, useDepositMethods } from '@/widgets';
import { INVOICE_FILE_TYPE } from '@/shared/config';
import type { InvoiceDetailsMode } from '@/features/deposit-bank-transfer';

export function useInvoiceDetails(
  invoiceData: InvoiceDetailsProps,
  mode: InvoiceDetailsMode
) {
  const { t } = useTranslation();
  const { closeDialog } = useModalStore();
  const { showBankTransferModal } = useDepositMethods();
  const { openInvoiceHistoryDialog } = useInvoiceHistoryDialog();
  const { banksData, isBanksLoading, isBanksError, banksError } =
    useBanksDictionary();
  const { toast, setPosition } = useToast();
  const {
    openModal,
    closeModal: closeConfirmationModal,
    Modal: ConfirmationModal,
  } = useConfirmationModal();

  const fileType = INVOICE_FILE_TYPE;

  const {
    data: fileData,
    isLoading: isFileLoading,
    error: fileError,
    isError: isFileError,
    download,
    print,
    getFileSize,
    isConvertingToPdf,
    conversionError,
  } = useInvoiceDownload({
    invoiceId: invoiceData.invoiceId,
    fileType,
    enabled: !!invoiceData.invoiceId,
  });

  const {
    data: invoiceDetails,
    isLoading,
    isError,
    error,
  } = useInvoice({
    invoiceId: invoiceData.invoiceId,
    enabled: !!invoiceData.invoiceId,
  });

  const deleteInvoiceMutation = useDeleteInvoice();

  const getBankName = (bankKey?: string) => {
    if (!bankKey || !banksData?.value?.deposit?.countries) {
      return t('depositMethods.bankTransfer.invoiceDetails.bankName');
    }

    for (const country of banksData.value.deposit.countries) {
      if (country.banks) {
        const bank = country.banks.find(
          (b: { bankKey: string; bankName: string }) => b.bankKey === bankKey
        );
        if (bank) {
          return bank.bankName;
        }
      }
    }

    return t('depositMethods.bankTransfer.invoiceDetails.bankName');
  };

  const getInvoiceFileName = () => {
    const bankName = getBankName(invoiceData.bankKey);
    return `${bankName.replace(/\s+/g, '_')}.pdf`;
  };

  const getFileSizeStr = (): string => {
    if (!fileData) return '';
    const size = getFileSize();
    return size ? `${(size / (1024 * 1024)).toFixed(2)} MB` : '';
  };

  const onPrint = () => {
    if (!fileData) return;
    print();
  };

  const onDownload = () => {
    if (!fileData) return;
    const fileName = getInvoiceFileName().replace('.html', '.pdf');
    download(fileName);
  };

  const handleDeleteClick = () => {
    openModal({
      title: t('confirmationModal.deleteInvoice.title'),
      description: t('confirmationModal.deleteInvoice.description'),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel'),
      onConfirm: async () => {
        let toastMessage = '';
        const toastConfig = {
          duration: 3000,
          showIcon: false,
          showCloseButton: false,
          position: 'top-center',
        };

        try {
          await deleteInvoiceMutation.mutateAsync({
            invoiceId: invoiceData.invoiceId,
          });
          toastMessage = t('confirmationModal.deleteInvoice.success');
        } catch {
          toastMessage = t('confirmationModal.deleteInvoice.error');
        } finally {
          closeConfirmationModal();

          if (toastMessage) {
            setPosition('top-center');
            toast.default(toastMessage, toastConfig);
          }

          openInvoiceHistoryDialog({
            onBack: showBankTransferModal,
            onClose: showBankTransferModal,
          });
        }
      },
    });
  };

  const handleSubmitButtonClick = () => {
    if (mode === 'view') {
      handleDeleteClick();
    } else {
      closeDialog();
    }
  };

  const getButtonText = () => {
    return mode === 'new'
      ? t('depositMethods.bankTransfer.invoiceDetails.done')
      : t('depositMethods.bankTransfer.invoiceDetails.delete');
  };

  const hasError = isError || isFileError || isBanksError || !!conversionError;
  let errorData = null;

  if (hasError) {
    if (isError) {
      errorData = error;
    } else if (isBanksError) {
      errorData = banksError;
    } else if (isFileError) {
      errorData = fileError;
    } else {
      errorData = new Error(t('common.unknownError'));
    }
  }

  return {
    // Data
    invoiceDetails,
    fileData,
    fileName: getInvoiceFileName(),
    fileSizeStr: getFileSizeStr(),

    // Loading states
    isLoading:
      isLoading || isFileLoading || isBanksLoading || isConvertingToPdf,
    isError: hasError,
    error: errorData,

    // Handlers
    onPrint,
    onDownload,
    handleSubmitButtonClick,

    // UI data
    buttonText: getButtonText(),

    // Components
    ConfirmationModal,
  };
}
